import React, {useEffect, useState} from "react";
import JsonViewer from "./JsonViewer";
import LoadingButton from "./LoadingButton";
import ABDMApi from "../ABDMApi";
import WorkflowStorage from "../ABDMWorkflowStorage";
import TokenStorage from "../ABDMStorage";
import {getValue} from "../utils/jsonPath";
export default function FlowCard({
                                     title = "",
                                     description = "",
                                     endpoint,
                                     method = "POST",
                                     fields = [],
                                     initialData = {},
                                     buttonText = "Execute",
                                     loadingText = "Processing...",
                                     prefill = {},
                                     save = {},
                                     requiredWorkflow = [],
                                     beforeSubmit = null,
                                     afterSuccess = null,
                                     afterError = null,
                                     onSuccess = null
                                 }) {
    // =========================================================
    // STATE
    // =========================================================
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [response, setResponse] = useState(null);
    const [status, setStatus] = useState("");
    const [elapsed, setElapsed] = useState(0);
    const [missingWorkflow, setMissingWorkflow] = useState([]);
    // =========================================================
    // LOAD WORKFLOW VALUES
    // =========================================================
    const loadWorkflow = () => {
        const workflow = WorkflowStorage.get() || {};
        const data = {
            ...initialData
        };
        // =====================================================
        // RESOLVE PREFILL VALUES
        // =====================================================
        Object.entries(prefill).forEach(([field, key]) => {
            // =================================================
            // 1. WORKFLOW STORAGE
            // =================================================
            if (workflow[key] !== undefined && workflow[key] !== null && workflow[key] !== "") {
                data[field] = workflow[key];
                return;
            }
            // =================================================
            // 2. TOKEN STORAGE - TXN ID
            // =================================================
            if (key === "txnId") {
                const txnId = TokenStorage.getTxnId();
                if (txnId !== undefined && txnId !== null && txnId !== "") {
                    data[field] = txnId;
                }
                return;
            }
            // =================================================
            // 3. TOKEN STORAGE - T TOKEN
            // =================================================
            if (key === "tToken") {
                const tToken = TokenStorage.getTToken();
                if (tToken !== undefined && tToken !== null && tToken !== "") {
                    data[field] = tToken;
                }
                return;
            }
            // =================================================
            // 4. TOKEN STORAGE - ABHA NUMBER
            // =================================================
            if (key === "abhaNumber" || key === "ABHANumber") {
                const abhaNumber = TokenStorage.getAbhaNumber();
                if (abhaNumber !== undefined && abhaNumber !== null && abhaNumber !== "") {
                    data[field] = abhaNumber;
                }
                return;
            }
            // =================================================
            // 5. TOKEN STORAGE - ABHA ADDRESS
            // =================================================
            if (key === "abhaAddress") {
                const abhaAddress = TokenStorage.getAbhaAddress();
                if (abhaAddress !== undefined && abhaAddress !== null && abhaAddress !== "") {
                    data[field] = abhaAddress;
                }
            }
        });
        // =====================================================
        // DEBUG
        // =====================================================
        console.log("========================================");
        console.log("FLOWCARD LOAD WORKFLOW");
        console.log("TITLE:", title);
        console.log("PREFILL:", prefill);
        console.log("WORKFLOW:", workflow);
        console.log("FORM DATA:", data);
        console.log("TXN ID FROM TOKEN STORAGE:", TokenStorage.getTxnId());
        console.log("T TOKEN FROM TOKEN STORAGE:", TokenStorage.getTToken());
        console.log("ABHA NUMBER FROM TOKEN STORAGE:", TokenStorage.getAbhaNumber());
        console.log("========================================");
        setFormData(data);
    };
    // =========================================================
    // CHECK REQUIRED WORKFLOW VALUES
    // =========================================================
    const checkWorkflow = () => {
        const missing = requiredWorkflow.filter(key => {
            // =============================================
            // FIRST: WORKFLOW STORAGE
            // =============================================
            const workflowValue = WorkflowStorage.getValue(key);
            if (workflowValue !== undefined && workflowValue !== null && workflowValue !== "") {
                return false;
            }
            // =============================================
            // SECOND: TOKEN STORAGE
            // =============================================
            if (key === "txnId") {
                const value = TokenStorage.getTxnId();
                return !value;
            }
            if (key === "tToken") {
                const value = TokenStorage.getTToken();
                return !value;
            }
            if (key === "abhaNumber" || key === "ABHANumber") {
                const value = TokenStorage.getAbhaNumber();
                return !value;
            }
            if (key === "abhaAddress") {
                const value = TokenStorage.getAbhaAddress();
                return !value;
            }
            // =============================================
            // UNKNOWN WORKFLOW VALUE
            // =============================================
            return true;
        });
        setMissingWorkflow(missing);
        console.log("========================================");
        console.log("FLOWCARD WORKFLOW CHECK");
        console.log("TITLE:", title);
        console.log("REQUIRED:", requiredWorkflow);
        console.log("MISSING:", missing);
        console.log("========================================");
    };
    // =========================================================
    // LISTEN WORKFLOW CHANGES
    // =========================================================
    useEffect(() => {
        const refresh = () => {
            loadWorkflow();
            checkWorkflow();
        };
        refresh();
        window.addEventListener("workflowChanged", refresh);
        return () => {
            window.removeEventListener("workflowChanged", refresh);
        };
    }, []);
    // =========================================================
    // FIELD CHANGE
    // =========================================================
    const handleChange = (name, value) => {
        setFormData(prev => ({
            ...prev, [name]: value
        }));
    };
    // =========================================================
    // VALIDATION
    // =========================================================
    const validate = () => {
        const err = {};
        fields.forEach(field => {
            if (field.required && !formData[field.name]) {
                err[field.name] = `${field.label} is required`;
            }
        });
        setErrors(err);
        return (Object.keys(err).length === 0);
    };
    // =========================================================
    // EXECUTE API
    // =========================================================
    const execute = async () => {
        // =====================================================
        // CHECK WORKFLOW
        // =====================================================
        if (missingWorkflow.length > 0) {
            alert("Run previous step first.\n\nMissing:\n\n" + missingWorkflow.join("\n"));
            return;
        }
        // =====================================================
        // VALIDATE
        // =====================================================
        if (!validate()) {
            return;
        }
        // =====================================================
        // BEFORE SUBMIT
        // =====================================================
        if (beforeSubmit) {
            beforeSubmit();
        }
        // =====================================================
        // LOADING
        // =====================================================
        setLoading(true);
        const start = performance.now();
        try {
            // =================================================
            // FINAL PAYLOAD
            // =================================================
            const payload = {
                ...formData
            };
            // =================================================
            // SAFETY FALLBACK
            //
            // If FlowCard somehow did not receive a value
            // from WorkflowStorage, read TokenStorage directly.
            // =================================================
            if (!payload.txnId) {
                const txnId = TokenStorage.getTxnId();
                if (txnId) {
                    payload.txnId = txnId;
                }
            }
            if (!payload.tToken) {
                const tToken = TokenStorage.getTToken();
                if (tToken) {
                    payload.tToken = tToken;
                }
            }
            if (!payload.ABHANumber) {
                const abhaNumber = TokenStorage.getAbhaNumber();
                if (abhaNumber) {
                    payload.ABHANumber = abhaNumber;
                }
            }
            // =================================================
            // DEBUG FINAL PAYLOAD
            // =================================================
            console.log("========================================");
            console.log("FLOWCARD API REQUEST");
            console.log("ENDPOINT:", endpoint);
            console.log("METHOD:", method);
            console.log("FINAL PAYLOAD:", payload);
            console.log("TXN ID:", payload.txnId);
            console.log("ABHA NUMBER:", payload.ABHANumber);
            console.log("T TOKEN EXISTS:", !!payload.tToken);
            console.log("T TOKEN LENGTH:", payload.tToken ? payload.tToken.length : 0);
            console.log("========================================");
            // =================================================
            // CALL ABDM API
            // =================================================
            const result = await ABDMApi({
                url: endpoint, method: method, data: payload
            });
            // =================================================
            // RESPONSE DEBUG
            // =================================================
            console.log("========================================");
            console.log("FLOWCARD RESULT");
            console.log(result);
            console.log("========================================");
            // =================================================
            // HTTP ERROR INSIDE RESPONSE
            // =================================================
            if (result.data?.status >= 400) {
                console.log("Calling afterError:", result.data);
                setStatus(result.data.status);
                setResponse(result.data);
                if (afterError) {
                    afterError(result.data);
                }
                return;
            }
            // =================================================
            // ELAPSED TIME
            // =================================================
            const end = performance.now();
            setElapsed(Math.round(end - start));
            // =================================================
            // STATUS
            // =================================================
            setStatus(result.status);
            // =================================================
            // RESPONSE
            // =================================================
            setResponse(result.data);
            // =================================================
            // SAVE WORKFLOW VALUES
            // =================================================
            Object.entries(save)
                .forEach(([storageKey, path]) => {
                    const value = getValue(result.data, path);
                    if (value !== undefined && value !== null) {
                        WorkflowStorage.save(storageKey, value);
                    }
                });
            // =================================================
            // CHECK WORKFLOW AGAIN
            // =================================================
            checkWorkflow();
            // =================================================
            // SUCCESS CALLBACKS
            // =================================================
            if (afterSuccess) {
                afterSuccess(result.data);
            }
            if (onSuccess) {
                onSuccess(result.data);
            }
        } catch (error) {
            // =================================================
            // ELAPSED
            // =================================================
            const end = performance.now();
            setElapsed(Math.round(end - start));
            // =================================================
            // ERROR RESPONSE
            // =================================================
            const errorResponse = error.response || {
                status: "ERROR", data: error.message
            };
            setStatus(errorResponse.status);
            setResponse(errorResponse.data);
            // =================================================
            // ERROR CALLBACK
            // =================================================
            if (afterError) {
                afterError(errorResponse);
            }
        } finally {
            setLoading(false);
        }
    };
    // =========================================================
    // RESET FORM
    // =========================================================
    const resetForm = () => {
        loadWorkflow();
        checkWorkflow();
    };
    // =========================================================
    // UI
    // =========================================================
    return (<div className="card shadow-sm mb-4">


        {/* =================================================
                HEADER
            ================================================= */}

        <div className="card-header bg-primary text-white">

            <strong>

                {title}

            </strong>

        </div>


        {/* =================================================
                BODY
            ================================================= */}

        <div className="card-body">


            {/* =================================================
                    DESCRIPTION
                ================================================= */}

            {description && (<p className="text-muted">

                {description}

            </p>)}


            {/* =================================================
                    MISSING WORKFLOW
                ================================================= */}

            {missingWorkflow.length > 0 && (<div className="alert alert-warning">

                <strong>

                    Previous step required

                </strong>


                <hr/>


                Missing Workflow Values


                <ul className="mb-0 mt-2">

                    {missingWorkflow.map(item => (<li
                        key={item}
                    >

                        {item}

                    </li>))}

                </ul>

            </div>)}


            {/* =================================================
                    FORM FIELDS
                ================================================= */}

            <div className="row">

                {fields
                    .filter(f => !f.hidden)
                    .map(field => (<div

                        key={field.name}

                        className={`col-md-${field.col || 6} mb-3`}

                    >


                        <label
                            className="form-label"
                        >

                            {field.label}


                            {field.required && (<span className="text-danger">

                                                *

                                            </span>)}

                        </label>


                        <input

                            type={field.type || "text"}

                            className={`form-control ${errors[field.name] ? "is-invalid" : ""}`}

                            value={formData[field.name] || ""}

                            placeholder={field.placeholder || ""}

                            readOnly={field.readOnly || !!prefill[field.name]}

                            maxLength={field.maxLength}

                            onChange={e => handleChange(field.name, e.target.value)}

                        />


                        {errors[field.name] && (<div className="invalid-feedback">

                            {errors[field.name]}

                        </div>)}

                    </div>))}

            </div>


            {/* =================================================
                    EXECUTE
                ================================================= */}

            <LoadingButton

                loading={loading}

                text={buttonText}

                loadingText={loadingText}

                disabled={missingWorkflow.length > 0}

                onClick={execute}

            />


            {/* =================================================
                    ACTIONS
                ================================================= */}

            <div className="d-flex gap-2 mt-3">


                <button

                    className="btn btn-outline-secondary"

                    onClick={resetForm}

                >

                    Reset

                </button>


                {response && (<button

                    className="btn btn-outline-success"

                    onClick={() => navigator.clipboard.writeText(JSON.stringify(response, null, 4))}

                >

                    Copy Response

                </button>)}

            </div>


            {/* =================================================
                    STATUS
                ================================================= */}

            {status !== "" && (<div className="mt-3">


                        <span

                            className={status >= 200 && status < 300 ? "badge bg-success" : "badge bg-danger"}

                        >

                            Status {status}

                        </span>


                <span className="badge bg-dark ms-2">

                            {elapsed} ms

                        </span>

            </div>)}


            {/* =================================================
                    RESPONSE
                ================================================= */}

            {response && (<JsonViewer

                title="Response"

                data={response}

            />)}

        </div>

    </div>);
}